import { usePermissions } from "@lib/hooks/usePermissions";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo } from "react";

import { TABLE_CONFIG } from "./constants";
import type { UseTableOptions, UseTableReturn } from "./types";
import { type ColumnDefWithPermissions, useTableColumns } from "./useTableColumns";
import { useTableData } from "./useTableData";
import { useTableErrorHandling } from "./useTableErrorHandling";
import { useTableMercure } from "./useTableMercure";
import { useTableState } from "./useTableState";
import { useTableVirtualization } from "./useTableVirtualization";
import { combineFilters } from "./utils";

/**
 * Main hook for creating a managed table with server-side pagination,
 * sorting, filtering, permission-based column visibility, and real-time updates.
 *
 * @typeParam TData — The row data type (default: Record<string, unknown>)
 */
export function useTable<TData extends Record<string, unknown> = Record<string, unknown>>(
  options: UseTableOptions<TData>,
): UseTableReturn<TData> {
  const {
    resource,
    columns,
    serverFilters: initialFilters = {},
    queryOptions = {},
    mercureEnabled = false,
    mercureTopic,
    virtualEnabled = false,
    defaultPageSize = TABLE_CONFIG.DEFAULT_PAGE_SIZE,
    enableRowSelection = false,
    virtualizerOptions = {},
    onError = "toast",
    silent = false,
    logger = null,
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  // --- Table state ---
  const tableState = useTableState({ defaultPageSize });

  // --- Permission-filtered columns ---
  const visibleColumns = useTableColumns<TData>(columns as ColumnDefWithPermissions<TData>[], can);

  // --- sortField map: column id -> backend OrderFilter property ---
  // Derived from the columns' `sortField` option. Falls back to the column id.
  const sortFieldMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const col of visibleColumns) {
      const withSort = col as ColumnDefWithPermissions<TData>;
      if (withSort.sortField && col.id) {
        map[col.id] = withSort.sortField;
      }
    }
    return map;
  }, [visibleColumns]);

  // --- Query key derived from all server-side parameters ---
  const queryKey = useMemo(
    () =>
      [
        resource,
        "list",
        {
          page: tableState.pagination.pageIndex + 1,
          itemsPerPage: tableState.pagination.pageSize,
          order: tableState.sorting.reduce<Record<string, string>>((acc, sort) => {
            acc[sortFieldMap[sort.id] ?? sort.id] = sort.desc ? "desc" : "asc";
            return acc;
          }, {}),
          filters: combineFilters(initialFilters, tableState.columnFilters),
          globalFilter: tableState.globalFilter,
        },
      ] as const,
    [
      resource,
      tableState.pagination,
      tableState.sorting,
      tableState.columnFilters,
      initialFilters,
      tableState.globalFilter,
      sortFieldMap,
    ],
  );

  // --- Error handling ---
  const { handleError } = useTableErrorHandling({
    onError,
    silent,
    navigate,
    location,
    logger,
  });

  // --- Data fetching ---
  const { data, isLoading, isFetching, error, refetch } = useTableData<TData>({
    resource,
    queryKey,
    queryOptions,
    pagination: tableState.pagination,
    sorting: tableState.sorting,
    initialFilters,
    columnFilters: tableState.columnFilters,
    globalFilter: tableState.globalFilter,
    sortFieldMap,
  });

  // Surface errors as side-effect
  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error, handleError]);

  const queryData: TData[] = (data?.member as TData[]) ?? [];
  const totalItems = data?.totalItems ?? 0;
  const pageCount = data ? Math.ceil(totalItems / tableState.pagination.pageSize) : -1;

  // --- Mercure real-time invalidation ---
  useTableMercure({
    resource,
    mercureTopic,
    enabled: mercureEnabled,
    queryKey,
  });

  // --- TanStack Table instance ---
  const table = useReactTable<TData>({
    data: queryData,
    columns: visibleColumns as ColumnDef<TData, any>[],
    pageCount,
    state: {
      pagination: tableState.pagination,
      sorting: tableState.sorting,
      columnFilters: tableState.columnFilters,
      rowSelection: tableState.rowSelection,
      globalFilter: tableState.globalFilter,
    },
    onPaginationChange: tableState.setPagination as OnChangeFn<PaginationState>,
    onSortingChange: tableState.setSorting as OnChangeFn<SortingState>,
    onColumnFiltersChange: tableState.setColumnFilters as OnChangeFn<ColumnFiltersState>,
    onRowSelectionChange: tableState.setRowSelection as OnChangeFn<RowSelectionState>,
    onGlobalFilterChange: tableState.setGlobalFilter as OnChangeFn<string>,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableRowSelection,
    getRowId: (row) => String(row.id),
  });

  // --- Virtualisation ---
  const { virtualizer, parentRef } = useTableVirtualization<TData>({
    table,
    enabled: virtualEnabled,
    options: virtualizerOptions,
  });

  // --- Row selection helpers ---
  // Computed directly each render — TanStack Table recreates the instance
  // every render, so useMemo/useCallback with `table` dep would be ineffective.
  const selectedRows: TData[] = table.getSelectedRowModel().rows.map((row) => row.original);

  const clearSelection = (): void => {
    table.resetRowSelection();
  };

  // --- Convenience helpers ---
  const sortBy = useCallback(
    (id: string, desc = false) => {
      table.setSorting([{ id, desc }]);
    },
    [table],
  );

  const filterBy = useCallback(
    (id: string, value: unknown) => {
      table.setColumnFilters((prev) => {
        const existing = prev.find((f) => f.id === id);
        if (existing) {
          return prev.map((f) => (f.id === id ? { ...f, value } : f));
        }
        return [...prev, { id, value }];
      });
    },
    [table],
  );

  const clearFilters = useCallback(() => {
    table.setColumnFilters([]);
    table.setGlobalFilter("");
  }, [table]);

  const resetAll = useCallback(() => {
    table.setSorting([]);
    table.setColumnFilters([]);
    table.setGlobalFilter("");
  }, [table]);

  return {
    table,
    data: queryData,
    totalItems,
    isLoading,
    isFetching,
    error,
    pageCount: table.getPageCount(),
    canNextPage: table.getCanNextPage(),
    canPreviousPage: table.getCanPreviousPage(),
    refetch,
    invalidate: () => queryClient.invalidateQueries({ queryKey }),
    virtualizer,
    parentRef,
    selectedRows,
    clearSelection,
    sortBy,
    filterBy,
    clearFilters,
    resetAll,
  };
}

export type { UseTableSearchOptions, UseTableSearchReturn } from "./useTableSearch";
export { useTableSearch } from "./useTableSearch";
